/* Bronson slideshow: fade/slide transitions, autoplay with progress ring, dots, arrows, swipe, theme editor support */
if (!customElements.get('bronson-slideshow')) {
  customElements.define(
    'bronson-slideshow',
    class BronsonSlideshow extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('[data-slide]')];
        this.dots = [...this.querySelectorAll('[data-dot]')];
        this.count = this.slides.length;
        this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
        this.autoplay = this.dataset.autoplay === 'true';
        this.delay = parseInt(this.dataset.delay, 10) || 6000;
        this.speed = parseInt(this.dataset.speed, 10) || 300;
        this.remaining = null;

        this.initImages();
        if (this.count < 2) return;

        this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, -1));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, 1));
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, i > this.index ? 1 : -1)));

        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') this.go(this.index - 1, -1);
          if (e.key === 'ArrowRight') this.go(this.index + 1, 1);
        });

        if (this.dataset.pauseHover === 'true') {
          this.addEventListener('mouseenter', () => this.pause());
          this.addEventListener('mouseleave', () => this.play());
        }
        // pause only for keyboard focus; mouse/touch clicks on arrows and dots keep autoplay running
        this.addEventListener('focusin', (e) => {
          if (e.target.matches(':focus-visible')) {
            this.keyboardPaused = true;
            this.pause();
          }
        });
        this.addEventListener('focusout', (e) => {
          if (this.keyboardPaused && !this.contains(e.relatedTarget)) {
            this.keyboardPaused = false;
            this.play();
          }
        });

        this.onVisibility = () => (document.hidden ? this.pause() : this.play());
        document.addEventListener('visibilitychange', this.onVisibility);

        if (this.dataset.swipe === 'true') this.initSwipe();
        this.initEditor();
        this.play();
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        clearTimeout(this.cleanupTimer);
        document.removeEventListener('visibilitychange', this.onVisibility);
        if (this.onBlockSelect) {
          document.removeEventListener('shopify:block:select', this.onBlockSelect);
          document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        }
      }

      initImages() {
        this.classList.add('is-ready');
        this.querySelectorAll('.bss-slide__img').forEach((img) => {
          const done = () => img.classList.add('is-loaded');
          if (img.complete && img.naturalWidth) done();
          else {
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
          }
        });
      }

      go(target, direction) {
        const next = ((target % this.count) + this.count) % this.count;
        if (next === this.index) return;
        const dir = direction || (next > this.index ? 1 : -1);
        this.finishTransition();

        const current = this.slides[this.index];
        const incoming = this.slides[next];
        const fromClass = dir > 0 ? 'from-next' : 'from-prev';

        incoming.classList.add('is-prep', fromClass);
        void incoming.offsetWidth;
        incoming.classList.remove('is-prep');
        incoming.classList.add('is-active', 'is-animating');

        current.classList.remove('is-active');
        current.classList.add('is-leaving', fromClass);

        this.pending = { current, incoming, fromClass };
        this.cleanupTimer = setTimeout(() => this.finishTransition(), this.speed + 40);

        this.slides.forEach((slide, i) => {
          const active = i === next;
          slide.toggleAttribute('inert', !active);
          slide.setAttribute('aria-hidden', String(!active));
        });

        this.index = next;
        this.updateDots();
        this.remaining = null;
        if (this.timer || !this.classList.contains('is-paused')) this.play();
      }

      finishTransition() {
        clearTimeout(this.cleanupTimer);
        if (!this.pending) return;
        const { current, incoming, fromClass } = this.pending;
        current.classList.remove('is-leaving', fromClass);
        incoming.classList.remove('is-animating', fromClass);
        this.pending = null;
      }

      updateDots() {
        this.dots.forEach((dot, i) => {
          const active = i === this.index;
          dot.classList.remove('is-active');
          dot.removeAttribute('aria-current');
          if (active) {
            void dot.offsetWidth; // restart the progress ring animation
            dot.classList.add('is-active');
            dot.setAttribute('aria-current', 'true');
          }
        });
      }

      play() {
        if (!this.autoplay || this.count < 2 || this.editorLocked) return;
        clearTimeout(this.timer);
        this.classList.remove('is-paused');
        this.startedAt = performance.now();
        const wait = this.remaining ?? this.delay;
        this.timer = setTimeout(() => {
          this.timer = null;
          this.go(this.index + 1, 1);
        }, Math.max(0, wait));
      }

      pause() {
        if (!this.autoplay) return;
        this.classList.add('is-paused');
        if (!this.timer) return;
        clearTimeout(this.timer);
        this.timer = null;
        const elapsed = performance.now() - this.startedAt;
        this.remaining = (this.remaining ?? this.delay) - elapsed;
      }

      initSwipe() {
        const viewport = this.querySelector('.bss__viewport');
        let startX = 0;
        let startY = 0;
        let tracking = false;
        let startTime = 0;

        viewport.addEventListener('pointerdown', (e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          if (e.target.closest('.bss-btn')) return;
          tracking = true;
          this.dragged = false;
          startX = e.clientX;
          startY = e.clientY;
          startTime = performance.now();
        });
        viewport.addEventListener('pointerup', (e) => {
          if (!tracking) return;
          tracking = false;
          const dx = e.clientX - startX;
          const dy = e.clientY - startY;
          if (Math.abs(dx) <= Math.abs(dy)) return;
          // quick flick moves one slide; a slow drag needs to pass the halfway point
          const elapsed = performance.now() - startTime;
          const ratio = viewport.clientWidth ? Math.abs(dx) / viewport.clientWidth : 0;
          const dir = Math.abs(dx) < 10 ? 0 : elapsed <= 300 || ratio >= 0.5 ? (dx < 0 ? 1 : -1) : 0;
          if (dir) {
            this.dragged = true;
            this.go(this.index + dir, dir);
          }
        });
        viewport.addEventListener('pointercancel', () => (tracking = false));
        viewport.addEventListener(
          'click',
          (e) => {
            if (this.dragged) {
              e.preventDefault();
              e.stopPropagation();
              this.dragged = false;
            }
          },
          true
        );
        viewport.addEventListener('dragstart', (e) => e.preventDefault());
      }

      initEditor() {
        if (!window.Shopify || !window.Shopify.designMode) return;
        this.onBlockSelect = (e) => {
          const slide = e.target.closest?.('[data-slide]');
          if (!slide || !this.contains(slide)) return;
          this.editorLocked = true;
          this.pause();
          const i = this.slides.indexOf(slide);
          this.go(i, i > this.index ? 1 : -1);
          this.pause();
        };
        this.onBlockDeselect = (e) => {
          if (!this.contains(e.target)) return;
          this.editorLocked = false;
          this.remaining = null;
          this.play();
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }
  );
}
